import type { SxProps, Theme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { StaffAutoComplete } from "../../../../components/UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { RecordSwitcher } from "../../../../components/UIToolkit/Navigation/RecordSwitcher/RecordSwitcher";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { useRosterLookup } from "../../../../hooks/useRosterLookup";
import type { Nurse } from "../../../../utils/models";

type Props = {
	/** The employee whose profile is open — the pill's label and the one excluded from the picker. */
	nurse: Nurse;
	/** Typography of the name, owned by the profile header. */
	sx?: SxProps<Theme>;
};

/**
 * The profile header's name, as a switcher: picking another employee opens their profile directly,
 * so reviewing several people no longer means a round trip through Team for each one (APP-535).
 */
export const NurseSwitcher: FC<Props> = ({ nurse, sx }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { roster, isLoading: isRosterLoading, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const formatPersonName = useFormatPersonName();
	const navigate = useNavigate();
	const location = useLocation();
	const { track } = useAnalytics();

	// The department's roster minus the profile already open — switching to it would be a no-op.
	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		() =>
			// No list yet: the picker is disabled until it loads, so there's nothing to offer.
			roster === undefined
				? []
				: roster.filter((candidate) => candidate.id !== nurse.id).map((candidate) => ({ label: formatPersonName(candidate), value: candidate })),
		[roster, nurse.id, formatPersonName],
	);

	return (
		<RecordSwitcher
			dataTestId="profile-nurse-switcher"
			label={formatPersonName(nurse)}
			ariaLabel={t("profile.switch.employee")}
			onOpenChange={(open) => open && track("profile_switch_employee_open", { nurse_id: nurse.id })}
			sx={sx}
			renderPicker={({ close }) => (
				<StaffAutoComplete
					fullWidth
					// The listbox renders inside the popover instead of portaling to <body>, so picking an
					// option isn't read as a click outside the popover (which would tear it down first).
					disablePortal
					disabled={isRosterLoading || isRosterError}
					helperText={isRosterError ? t("profile.roster.unavailable") : undefined}
					dataTestId="profile-nurse-switcher-search"
					nurses={nurseOptions}
					value={null}
					onChange={(_event, option) => {
						if (!option) {
							return;
						}
						close();
						track("profile_switch_employee", { from_nurse_id: nurse.id, to_nurse_id: option.value.id });
						navigate(`/${chosenDepartment.department_id}/nurse/${option.value.id}`, {
							// Carry the entry point through the jump so Back still returns where the user came
							// from (Team), not to the profile they were just on. When there is none to carry (a
							// profile opened straight from a URL), the profile hook falls back to this
							// department's Team page rather than a bare "/team", which is not a route.
							state: { navigationSrc: (location.state as { navigationSrc?: string } | null)?.navigationSrc },
						});
					}}
				/>
			)}
		/>
	);
};
